import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useHousehold } from "../auth/AuthProvider";
import { useLocation } from "react-router";
import { isDraftDetail } from "./householdSync";
import { get, hpath, qs } from "./api";
import type {
  ApiKey,
  Category,
  CookSession,
  GroceryList,
  GroceryListSummary,
  HouseholdDetail,
  Invitation,
  Menu,
  Paged,
  PantryItem,
  PlanEntry,
  Recipe,
  RecipeSummary,
  Timer,
} from "./types";

export const keys = {
  all: (hid: string) => ["h", hid] as const,
  recipes: (hid: string, params?: object) => ["h", hid, "recipes", params ?? {}] as const,
  recipe: (hid: string, id: string) => ["h", hid, "recipe", id] as const,
  categories: (hid: string) => ["h", hid, "categories"] as const,
  groceryLists: (hid: string) => ["h", hid, "grocery-lists"] as const,
  groceryList: (hid: string, id: string) => ["h", hid, "grocery-list", id] as const,
  pantry: (hid: string, params?: object) => ["h", hid, "pantry", params ?? {}] as const,
  plan: (hid: string, from: string, to: string) => ["h", hid, "plan", from, to] as const,
  menus: (hid: string) => ["h", hid, "menus"] as const,
  menu: (hid: string, id: string) => ["h", hid, "menu", id] as const,
  cookSessions: (hid: string) => ["h", hid, "cook-sessions"] as const,
  cookSession: (hid: string, recipeId: string) => ["h", hid, "cook-session", recipeId] as const,
  timers: (hid: string) => ["h", hid, "timers"] as const,
  household: (hid: string) => ["h", hid, "detail"] as const,
  invitations: (hid: string) => ["h", hid, "invitations"] as const,
  apiKeys: (hid: string) => ["h", hid, "api-keys"] as const,
};

export interface RecipeListParams {
  q?: string;
  category?: string;
  favorite?: "true";
  trashed?: "true";
  sort?: "title" | "updated" | "created" | "rating" | "time";
  order?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export function useRecipes(params: RecipeListParams, enabled = true) {
  const { householdId } = useHousehold();
  return useQuery({
    queryKey: keys.recipes(householdId, params),
    queryFn: ({ signal }) => get<Paged<RecipeSummary>>(hpath(householdId, `/recipes${qs({ ...params })}`), signal),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useRecipe(id: string | undefined) {
  const { householdId } = useHousehold();
  const { pathname } = useLocation();
  return useQuery({
    queryKey: keys.recipe(householdId, id ?? ""),
    // The editor owns its starting snapshot and expectedVersion until an explicit save/conflict action.
    refetchOnMount: !isDraftDetail(keys.recipe(householdId, id ?? ""), pathname),
    queryFn: ({ signal }) => get<Recipe>(hpath(householdId, `/recipes/${id}`), signal),
    enabled: !!id,
  });
}

export function useCategories() {
  const { householdId } = useHousehold();
  return useQuery({
    queryKey: keys.categories(householdId),
    queryFn: ({ signal }) => get<{ items: Category[] }>(hpath(householdId, "/categories"), signal),
    select: (data) => data.items,
  });
}

export function useGroceryLists() {
  const { householdId } = useHousehold();
  return useQuery({
    queryKey: keys.groceryLists(householdId),
    queryFn: ({ signal }) => get<Paged<GroceryListSummary>>(hpath(householdId, "/grocery-lists?limit=200"), signal),
  });
}

export function useGroceryList(id: string | undefined) {
  const { householdId } = useHousehold();
  return useQuery({
    queryKey: keys.groceryList(householdId, id ?? ""),
    queryFn: ({ signal }) => get<GroceryList>(hpath(householdId, `/grocery-lists/${id}`), signal),
    enabled: !!id,
  });
}

export interface PantryParams {
  q?: string;
  location?: string;
  status?: "all" | "expired" | "expiring";
  sort?: "name" | "expires" | "location" | "updated";
  today: string;
  limit?: number;
  offset?: number;
}

export function usePantry(params: PantryParams) {
  const { householdId } = useHousehold();
  return useQuery({
    queryKey: keys.pantry(householdId, params),
    queryFn: ({ signal }) => get<Paged<PantryItem> & { locations: string[] }>(hpath(householdId, `/pantry${qs({ ...params })}`), signal),
    placeholderData: keepPreviousData,
  });
}

export function usePlan(from: string, to: string) {
  const { householdId } = useHousehold();
  return useQuery({
    queryKey: keys.plan(householdId, from, to),
    queryFn: ({ signal }) => get<{ from: string; to: string; entries: PlanEntry[] }>(hpath(householdId, `/meal-plan${qs({ from, to })}`), signal),
    placeholderData: keepPreviousData,
  });
}

export function useMenus() {
  const { householdId } = useHousehold();
  return useQuery({
    queryKey: keys.menus(householdId),
    queryFn: ({ signal }) => get<Paged<Menu>>(hpath(householdId, "/menus?limit=200"), signal),
  });
}

export function useMenu(id: string | undefined) {
  const { householdId } = useHousehold();
  const { pathname } = useLocation();
  return useQuery({
    queryKey: keys.menu(householdId, id ?? ""),
    refetchOnMount: !isDraftDetail(keys.menu(householdId, id ?? ""), pathname),
    queryFn: ({ signal }) => get<Menu>(hpath(householdId, `/menus/${id}`), signal),
    enabled: !!id,
  });
}

export function useCookSessions() {
  const { householdId } = useHousehold();
  return useQuery({
    queryKey: keys.cookSessions(householdId),
    queryFn: ({ signal }) => get<{ items: CookSession[] }>(hpath(householdId, "/cook-sessions"), signal),
    select: (data) => data.items,
    staleTime: 15_000,
  });
}

export function useTimers() {
  const { householdId } = useHousehold();
  return useQuery({
    queryKey: keys.timers(householdId),
    queryFn: ({ signal }) => get<{ items: Timer[] }>(hpath(householdId, "/timers"), signal),
    select: (data) => data.items,
    // Refreshes through the same write-aware visible-page scheduler as other household data.
    staleTime: 5_000,
  });
}

export function useHouseholdDetail() {
  const { householdId } = useHousehold();
  return useQuery({
    queryKey: keys.household(householdId),
    queryFn: ({ signal }) => get<HouseholdDetail>(hpath(householdId), signal),
  });
}

export function useInvitations(enabled: boolean) {
  const { householdId } = useHousehold();
  return useQuery({
    queryKey: keys.invitations(householdId),
    queryFn: ({ signal }) => get<{ items: Invitation[] }>(hpath(householdId, "/invitations"), signal),
    select: (data) => data.items,
    enabled,
  });
}

export function useApiKeys(enabled: boolean) {
  const { householdId } = useHousehold();
  return useQuery({
    queryKey: keys.apiKeys(householdId),
    queryFn: ({ signal }) => get<{ items: ApiKey[] }>(hpath(householdId, "/api-keys"), signal),
    select: (data) => data.items,
    enabled,
  });
}

/** Invalidate everything in the active household (after bulk-affecting writes). */
export function useInvalidateHousehold() {
  const queryClient = useQueryClient();
  const { householdId } = useHousehold();
  return () => queryClient.invalidateQueries({ queryKey: keys.all(householdId) });
}
